import type { PromotionTarget, PromotionTargetGroup, TranslationKey } from '@dukanti/shared';
import { useTranslation } from '@dukanti/shared';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export function PromotionTargetPicker(props: {
  readonly groups: readonly PromotionTargetGroup[];
  readonly selectedTargetId: string | null;
  readonly search: string;
  readonly onSearchChange: (value: string) => void;
  readonly onSelect: (target: PromotionTarget) => void;
  readonly onLoadMore: () => void;
  readonly hasMore: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <section className="border-border bg-card shadow-admin-card rounded-lg border p-4">
      <label className="text-foreground text-sm font-medium" htmlFor="promotion-target-search">
        {t('admin.promotionKit.target.label' as TranslationKey)}
      </label>
      <div className="relative mt-2">
        <Search className="text-muted-foreground pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2" />
        <Input
          id="promotion-target-search"
          value={props.search}
          onChange={(event) => props.onSearchChange(event.target.value)}
          placeholder={t('admin.promotionKit.target.search' as TranslationKey)}
          className="ps-10"
        />
      </div>
      <div className="mt-4 space-y-4">
        {props.groups.map((group) => (
          <div key={group.type}>
            <h3 className="text-muted-foreground mb-2 text-start text-xs font-semibold uppercase tracking-normal">
              {t(group.labelKey)}
            </h3>
            {group.targets.length > 0 ? (
              <div className="grid gap-2">
                {group.targets.map((target) => (
                  <button
                    key={target.id}
                    type="button"
                    onClick={() => props.onSelect(target)}
                    className={[
                      'border-border bg-background hover:bg-admin-surface-muted rounded-lg border p-3 text-start transition',
                      props.selectedTargetId === target.id ? 'ring-admin-lime ring-2' : '',
                    ].join(' ')}
                  >
                    <span className="text-foreground block text-sm font-semibold">
                      {target.title}
                    </span>
                    {target.subtitle ? (
                      <span className="text-muted-foreground mt-1 line-clamp-2 block text-xs">
                        {target.subtitle}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            ) : group.emptyKey ? (
              <p className="text-muted-foreground text-sm">{t(group.emptyKey)}</p>
            ) : null}
          </div>
        ))}
      </div>
      {props.hasMore ? (
        <Button type="button" variant="outline" className="mt-4 w-full" onClick={props.onLoadMore}>
          {t('admin.promotionKit.target.loadMore' as TranslationKey)}
        </Button>
      ) : null}
    </section>
  );
}
